import React, { useState } from "react";
import MenuItem from "@mui/material/MenuItem";
import Menu from "@mui/material/Menu";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Add from "@mui/icons-material/Add";
import ArrowUpward from "@mui/icons-material/ArrowUpward";
import ArrowDownward from "@mui/icons-material/ArrowDownward";
import FormatListNumbered from "@mui/icons-material/FormatListNumbered";
import FormatListBulleted from "@mui/icons-material/FormatListNumbered";
import Title from "@mui/icons-material/Title";
import Link from "@mui/icons-material/Link";
import Image from "@mui/icons-material/Image";
import Delete from "@mui/icons-material/Delete";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { ContentBlock } from "../types/editor";

interface BlockEditorProps {
  value: ContentBlock[];
  onChange: (blocks: ContentBlock[]) => void;
  language: "en" | "ru";
}

const BlockEditor: React.FC<BlockEditorProps> = ({
  value,
  onChange,
  language,
}) => {
  const [blocks, setBlocks] = useState<ContentBlock[]>(value || []);
  const [selectedBlockIndex, setSelectedBlockIndex] = useState<number | null>(
    null
  );
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleAddBlock = (type: ContentBlock["type"]) => {
    const newBlock: ContentBlock = {
      type,
      text: "",
      items: type === "bullets" || type === "numbered" ? [""] : undefined,
    };
    const newBlocks = [...blocks, newBlock];
    setBlocks(newBlocks);
    onChange(newBlocks);
    setSelectedBlockIndex(newBlocks.length - 1);
    setAnchorEl(null);
  };

  const handleUpdateBlock = (
    index: number,
    updatedBlock: Partial<ContentBlock>
  ) => {
    const newBlocks = [...blocks];
    newBlocks[index] = { ...newBlocks[index], ...updatedBlock };
    setBlocks(newBlocks);
    onChange(newBlocks);
  };

  const handleDeleteBlock = (index: number) => {
    const newBlocks = blocks.filter((_, i) => i !== index);
    setBlocks(newBlocks);
    onChange(newBlocks);
    setSelectedBlockIndex(null);
  };

  const handleMoveBlock = (index: number, direction: "up" | "down") => {
    const newBlocks = [...blocks];
    const targetIndex = direction === "up" ? index - 1 : index + 1;

    if (targetIndex >= 0 && targetIndex < newBlocks.length) {
      [newBlocks[index], newBlocks[targetIndex]] = [
        newBlocks[targetIndex],
        newBlocks[index],
      ];
      setBlocks(newBlocks);
      onChange(newBlocks);
      setSelectedBlockIndex(targetIndex);
    }
  };

  const handleAddListItem = (blockIndex: number) => {
    const block = blocks[blockIndex];
    if (block.items) {
      const newItems = [...block.items, ""];
      handleUpdateBlock(blockIndex, { items: newItems });
    }
  };

  const handleUpdateListItem = (
    blockIndex: number,
    itemIndex: number,
    value: string
  ) => {
    const block = blocks[blockIndex];
    if (block.items) {
      const newItems = [...block.items];
      newItems[itemIndex] = value;
      handleUpdateBlock(blockIndex, { items: newItems });
    }
  };

  const handleDeleteListItem = (blockIndex: number, itemIndex: number) => {
    const block = blocks[blockIndex];
    if (block.items && block.items.length > 1) {
      const newItems = block.items.filter((_, i) => i !== itemIndex);
      handleUpdateBlock(blockIndex, { items: newItems });
    }
  };

  const renderBlock = (block: ContentBlock, index: number) => {
    const isSelected = selectedBlockIndex === index;

    return (
      <Paper
        key={index}
        elevation={isSelected ? 3 : 1}
        sx={{
          p: 2,
          mb: 2,
          border: isSelected ? "2px solid #1976d2" : "1px solid #e0e0e0",
          cursor: "pointer",
        }}
        onClick={() => setSelectedBlockIndex(index)}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1,
          }}
        >
          <Typography variant="caption" color="text.secondary">
            {block.type.toUpperCase()}
          </Typography>
          <Box>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleMoveBlock(index, "up");
              }}
              disabled={index === 0}
            >
              <ArrowUpward />
            </IconButton>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleMoveBlock(index, "down");
              }}
              disabled={index === blocks.length - 1}
            >
              <ArrowDownward />
            </IconButton>
            <IconButton
              size="small"
              color="error"
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteBlock(index);
              }}
            >
              <Delete />
            </IconButton>
          </Box>
        </Box>

        {block.type === "paragraph" && (
          <ReactQuill
            value={block.text || ""}
            onChange={(value) => handleUpdateBlock(index, { text: value })}
            modules={{
              toolbar: [
                ["bold", "italic", "underline"],
                ["link"],
                [{ list: "ordered" }, { list: "bullet" }],
                ["clean"],
              ],
            }}
            theme="snow"
            style={{ minHeight: "100px" }}
          />
        )}

        {block.type === "subtitle" && (
          <TextField
            fullWidth
            value={block.text || ""}
            onChange={(e) => handleUpdateBlock(index, { text: e.target.value })}
            placeholder="Enter subtitle..."
            variant="outlined"
            multiline
          />
        )}

        {block.type === "link" && (
          <Box sx={{ display: "flex", gap: 1, flexDirection: "column" }}>
            <TextField
              fullWidth
              value={block.text || ""}
              onChange={(e) =>
                handleUpdateBlock(index, { text: e.target.value })
              }
              placeholder="Link text..."
              variant="outlined"
            />
            <TextField
              fullWidth
              value={block.href || ""}
              onChange={(e) =>
                handleUpdateBlock(index, { href: e.target.value })
              }
              placeholder="https://example.com"
              variant="outlined"
            />
          </Box>
        )}

        {block.type === "image" && (
          <Box sx={{ display: "flex", gap: 1, flexDirection: "column" }}>
            <TextField
              fullWidth
              value={block.src || ""}
              onChange={(e) =>
                handleUpdateBlock(index, { src: e.target.value })
              }
              placeholder="Image URL..."
              variant="outlined"
            />
            <TextField
              fullWidth
              value={block.alt || ""}
              onChange={(e) =>
                handleUpdateBlock(index, { alt: e.target.value })
              }
              placeholder="Alt text..."
              variant="outlined"
            />
          </Box>
        )}

        {(block.type === "bullets" || block.type === "numbered") && (
          <Box>
            {block.items?.map((item, itemIndex) => (
              <Box key={itemIndex} sx={{ display: "flex", gap: 1, mb: 1 }}>
                <TextField
                  fullWidth
                  value={item}
                  onChange={(e) =>
                    handleUpdateListItem(index, itemIndex, e.target.value)
                  }
                  placeholder={`Item ${itemIndex + 1}...`}
                  variant="outlined"
                  size="small"
                />
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => handleDeleteListItem(index, itemIndex)}
                  disabled={block.items && block.items.length <= 1}
                >
                  <Delete />
                </IconButton>
              </Box>
            ))}
            <Button
              size="small"
              startIcon={<Add />}
              onClick={() => handleAddListItem(index)}
            >
              Add Item
            </Button>
          </Box>
        )}
      </Paper>
    );
  };

  return (
    <Box>
      <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={(e) => setAnchorEl(e.currentTarget)}
        >
          Add Block
        </Button>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
        >
          <MenuItem onClick={() => handleAddBlock("paragraph")}>
            <Title sx={{ mr: 1 }} />
            Paragraph
          </MenuItem>
          <MenuItem onClick={() => handleAddBlock("subtitle")}>
            <Title sx={{ mr: 1 }} />
            Subtitle
          </MenuItem>
          <MenuItem onClick={() => handleAddBlock("bullets")}>
            <FormatListBulleted sx={{ mr: 1 }} />
            Bullet List
          </MenuItem>
          <MenuItem onClick={() => handleAddBlock("numbered")}>
            <FormatListNumbered sx={{ mr: 1 }} />
            Numbered List
          </MenuItem>
          <MenuItem onClick={() => handleAddBlock("link")}>
            <Link sx={{ mr: 1 }} />
            Link
          </MenuItem>
          <MenuItem onClick={() => handleAddBlock("image")}>
            <Image sx={{ mr: 1 }} />
            Image
          </MenuItem>
        </Menu>
      </Box>

      {blocks.length === 0 && (
        <Paper
          sx={{
            p: 4,
            textAlign: "center",
            border: "2px dashed #e0e0e0",
            color: "text.secondary",
          }}
        >
          <Typography variant="body1">
            No content blocks yet. Click "Add Block" to get started.
          </Typography>
        </Paper>
      )}

      {blocks.map((block, index) => renderBlock(block, index))}
    </Box>
  );
};

export default BlockEditor;
